import type {
  BoxListItem,
  BoxProductItem,
  IWarehouseApi,
} from "../../../api/WarehouseApi";
import type { Box as BoxModel } from "../../../stores/Box";
import type { INavigationStore } from "../../../stores/navigation";
import { deleteProductWithDestroyJournal } from "../../shared/use-cases/delete-flow";
import { deleteCurrentBoxAndNavigate } from "./current-box-delete";
import {
  deleteChildBox as deleteChildBoxMutation,
  detachChildBox as detachChildBoxMutation,
  removeProductFromBox as removeProductFromBoxMutation,
} from "./mutations";
import {
  openBoxByIdWithActionError,
  openHistoryProductById,
} from "./reference-navigation";

function resolveErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export interface BoxPageOperationBindings {
  api: IWarehouseApi;
  navigationStore: INavigationStore;
  box: BoxModel;
  isNestedMutationLocked: () => boolean;
  canDeleteCurrentBoxNow: () => boolean;
  setBusyProductId: (id: string | null) => void;
  setBusyBoxId: (id: string | null) => void;
  setIsDeletingCurrentBox: (value: boolean) => void;
  clearActionError: () => void;
  setActionError: (message: string) => void;
  reload: () => Promise<void>;
}

async function runWithBusyProduct(input: {
  bindings: BoxPageOperationBindings;
  productId: string;
  fallbackError: string;
  run: () => Promise<void>;
  reloadOnSuccess?: boolean;
}): Promise<void> {
  if (input.bindings.isNestedMutationLocked()) {
    return;
  }

  input.bindings.setBusyProductId(input.productId);
  input.bindings.clearActionError();

  try {
    await input.run();
    if (input.reloadOnSuccess !== false) {
      await input.bindings.reload();
    }
  } catch (error) {
    input.bindings.setActionError(
      resolveErrorMessage(error, input.fallbackError),
    );
  } finally {
    input.bindings.setBusyProductId(null);
  }
}

async function runWithBusyBox(input: {
  bindings: BoxPageOperationBindings;
  boxId: string;
  fallbackError: string;
  run: () => Promise<void>;
  reloadOnSuccess?: boolean;
}): Promise<void> {
  if (input.bindings.isNestedMutationLocked()) {
    return;
  }

  input.bindings.setBusyBoxId(input.boxId);
  input.bindings.clearActionError();

  try {
    await input.run();
    if (input.reloadOnSuccess !== false) {
      await input.bindings.reload();
    }
  } catch (error) {
    input.bindings.setActionError(
      resolveErrorMessage(error, input.fallbackError),
    );
  } finally {
    input.bindings.setBusyBoxId(null);
  }
}

export async function executeRemoveProductFromBox(input: {
  bindings: BoxPageOperationBindings;
  item: BoxProductItem;
}): Promise<void> {
  await runWithBusyProduct({
    bindings: input.bindings,
    productId: input.item.id,
    fallbackError: "Не удалось убрать продукт из коробки",
    run: async () => {
      await removeProductFromBoxMutation({
        api: input.bindings.api,
        boxId: input.bindings.box.id,
        item: input.item,
      });
    },
  });
}

export async function executeDeleteProductFromBox(input: {
  bindings: BoxPageOperationBindings;
  item: BoxProductItem;
  deleteReason?: string | null;
}): Promise<void> {
  await runWithBusyProduct({
    bindings: input.bindings,
    productId: input.item.id,
    fallbackError: "Не удалось удалить продукт",
    run: async () => {
      await deleteProductWithDestroyJournal({
        api: input.bindings.api,
        productId: input.item.id,
        sourceBoxId: input.bindings.box.id,
        deleteReason: input.deleteReason,
        sourceLabel: "с экрана коробки",
      });
    },
  });
}

export async function executeDetachChildBox(input: {
  bindings: BoxPageOperationBindings;
  item: BoxListItem;
}): Promise<void> {
  await runWithBusyBox({
    bindings: input.bindings,
    boxId: input.item.id,
    fallbackError: "Не удалось убрать вложенную коробку",
    run: async () => {
      await detachChildBoxMutation({
        api: input.bindings.api,
        item: input.item,
      });
    },
  });
}

export async function executeDeleteChildBox(input: {
  bindings: BoxPageOperationBindings;
  item: BoxListItem;
  deleteReason?: string | null;
}): Promise<void> {
  await runWithBusyBox({
    bindings: input.bindings,
    boxId: input.item.id,
    fallbackError: "Не удалось удалить коробку",
    run: async () => {
      await deleteChildBoxMutation({
        api: input.bindings.api,
        childBoxId: input.item.id,
        deleteReason: input.deleteReason,
      });
    },
  });
}

export async function executeDeleteCurrentBox(input: {
  bindings: BoxPageOperationBindings;
  deleteReason?: string | null;
}): Promise<void> {
  if (!input.bindings.canDeleteCurrentBoxNow()) {
    return;
  }

  input.bindings.setIsDeletingCurrentBox(true);
  input.bindings.clearActionError();

  try {
    await deleteCurrentBoxAndNavigate({
      api: input.bindings.api,
      navigationStore: input.bindings.navigationStore,
      box: input.bindings.box,
      deleteReason: input.deleteReason,
    });
  } catch (error) {
    input.bindings.setActionError(
      resolveErrorMessage(error, "Не удалось уничтожить коробку"),
    );
  } finally {
    input.bindings.setIsDeletingCurrentBox(false);
  }
}

export async function executeOpenHistoryProduct(input: {
  bindings: BoxPageOperationBindings;
  productId: string;
}): Promise<void> {
  const trimmedProductId = input.productId.trim();
  if (!trimmedProductId) {
    return;
  }

  input.bindings.setBusyProductId(trimmedProductId);
  input.bindings.clearActionError();
  try {
    await openHistoryProductById({
      api: input.bindings.api,
      navigationStore: input.bindings.navigationStore,
      productId: trimmedProductId,
      setActionError: (message) => {
        input.bindings.setActionError(message);
      },
    });
  } finally {
    input.bindings.setBusyProductId(null);
  }
}

export async function executeOpenHistoryBox(input: {
  bindings: BoxPageOperationBindings;
  boxId: string;
}): Promise<void> {
  const trimmedBoxId = input.boxId.trim();
  if (!trimmedBoxId) {
    return;
  }

  input.bindings.setBusyBoxId(trimmedBoxId);
  input.bindings.clearActionError();
  try {
    await openBoxByIdWithActionError({
      api: input.bindings.api,
      navigationStore: input.bindings.navigationStore,
      boxId: trimmedBoxId,
      setActionError: (message) => {
        input.bindings.setActionError(message);
      },
    });
  } finally {
    input.bindings.setBusyBoxId(null);
  }
}
